import React, { useState, useEffect } from 'react';
import FormBuilder from './components/FormBuilder';
import FormLibrary from './components/FormLibrary';
import ResponseAnalytics from './components/ResponseAnalytics';
import useFeedbackManagementStore from '@/stores/AdminStore/FeedbackManagementStore';
import './SuperAdminFeedbackManagement.css';

type TabType = 'builder' | 'library' | 'analytics';

interface TabItem {
  id: TabType;
  label: string;
}

const FeedbackManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('builder');

  const {
    surveys,
    analyticsStats,
    barChartData,
    ratingDistribution,
    recentResponses,
    surveyOptions,
    fetchSurveys,
    fetchAnalyticsStats,
    fetchBarChartData,
    fetchRatingDistribution,
    fetchRecentResponses,
    fetchSurveyOptions,
  } = useFeedbackManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'library' && !surveys) {
      fetchSurveys();
    }
    if (activeTab === 'analytics' && !analyticsStats) {
      fetchAnalyticsStats();
    }
    if (activeTab === 'analytics' && !barChartData) {
      fetchBarChartData();
    }
    if (activeTab === 'analytics' && !ratingDistribution) {
      fetchRatingDistribution();
    }
    if (activeTab === 'analytics' && !recentResponses) {
      fetchRecentResponses();
    }
    if (activeTab === 'analytics' && !surveyOptions) {
      fetchSurveyOptions();
    }
  }, [
    activeTab,
    surveys,
    analyticsStats,
    barChartData,
    ratingDistribution,
    recentResponses,
    surveyOptions,
    fetchSurveys,
    fetchAnalyticsStats,
    fetchBarChartData,
    fetchRatingDistribution,
    fetchRecentResponses,
    fetchSurveyOptions,
  ]);

  const tabs: TabItem[] = [
    { id: 'builder', label: 'Form Builder' },
    { id: 'library', label: 'Form Library' },
    { id: 'analytics', label: 'Response Analytics' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'builder':
        return <FormBuilder />;
      case 'library':
        return <FormLibrary />;
      case 'analytics':
        return <ResponseAnalytics />;
      default:
        return <FormBuilder />;
    }
  };

  return (
    <div className="s-admin-feedback-management">
      <div className="s-admin-feedback-header">
        <div className="s-admin-feedback-breadcrumb">
          <span>Tools</span>
          <span className="s-admin-feedback-breadcrumb-separator">›</span>
          <span>Feedback</span>
        </div>
        <h1 className="s-admin-feedback-title">Tools</h1>
        <p className="s-admin-feedback-subtitle">Administrative utilities and system tools</p>
      </div>

      <div className="s-admin-feedback-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`s-admin-feedback-tab ${activeTab === tab.id ? 's-admin-feedback-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="s-admin-feedback-content">{renderTabContent()}</div>
    </div>
  );
};

export default FeedbackManagement;
